import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
  Checkbox,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Container } from "@mui/system";
import React, { useEffect, useRef } from "react";
import TableItem, { TableItemProps } from "./TableItem";
import {
  CountyFacilities,
  Facility,
  FacilityProgramProps,
  FACILITY_TYPE_CAC,
  FACILITY_TYPE_OTHER,
  FACILITY_TYPE_RCC,
  FACILITY_TYPE_TT,
} from "./types";

import { ALL_COUNTY_NAMES } from "./utils";

type TableFacility = TableItemProps["facility"];

export const FILTER_SANE_ADULT_ACUTE = "SANE - Acute Adult";
export const FILTER_SANE_ADULT_NON_ACUTE = "SANE - Non-Acute Adult";
export const FILTER_SANE_ADOLESCENT_ACUTE = "SANE - Acute Adolescent";
export const FILTER_SANE_ADOLESCENT_NON_ACUTE = "SANE - Non-Acute Adolescent";
export const FILTER_SANE_PEDIATRIC_ACUTE = "SANE - Acute Pediatric";
export const FILTER_SANE_PEDIATRIC_NON_ACUTE = "SANE - Non-Acute Pediatric";
export const FILTER_SANE_CHILD_ABUSE = "SANE - Child Abuse";
export const FILTER_SANE_IPV = "SANE - IPV";
export const FILTER_SANE_ELDER = "SANE - Elder Maltreatment";
export const FILTER_SANE_TRAUMA = "SANE - Trauma";
export const FILTER_SANE_SUSPECT = "SANE - Suspect";
export const FILTER_ALL_SANE = "SANE";

function Table({
  counties,
  onSelectCounty: setSelectedCounty,
  selectedCounty,
  isMobile,
}: {
  counties: CountyFacilities;
  onSelectCounty: (p: string) => void;
  selectedCounty?: string;
  isMobile: boolean;
}) {
  const theme = useTheme();

  const programMenuLabels: (string | [FacilityProgramProps, string])[] = [
    FILTER_ALL_SANE,
    ["adultAcute", FILTER_SANE_ADULT_ACUTE],
    ["adultNonAcute", FILTER_SANE_ADULT_NON_ACUTE],
    ["adolescentAcute", FILTER_SANE_ADOLESCENT_ACUTE],
    ["adolescentNonAcute", FILTER_SANE_ADOLESCENT_NON_ACUTE],
    ["pediatricAcute", FILTER_SANE_PEDIATRIC_ACUTE],
    ["pediatricNonAcute", FILTER_SANE_PEDIATRIC_NON_ACUTE],
    ["child", FILTER_SANE_CHILD_ABUSE],
    ["ipv", FILTER_SANE_IPV],
    ["elder", FILTER_SANE_ELDER],
    ["trauma", FILTER_SANE_TRAUMA],
    ["suspect", FILTER_SANE_SUSPECT],
    FACILITY_TYPE_TT,
    FACILITY_TYPE_CAC,
    FACILITY_TYPE_RCC,
  ];
  const numFacilityLabels = 3;
  const sanePrograms = programMenuLabels.slice(1, -numFacilityLabels);

  const [programFilters, setProgramFilters] = React.useState<string[]>([]);

  function compareProgramStringToFilter(programs: {
    [program: string]: string;
  }) {
    if (!programFilters.length) {
      return true;
    }

    for (let filter of programFilters) {
      if (programs[filter]) {
        return true;
      }
    }
    return false;
  }

  function sortFacilities(f1: Facility, f2: Facility) {
    let f1Score = priorityScore(f1);
    let f2Score = priorityScore(f2);
  
    if (f1Score === f2Score) {
      return f1.name.localeCompare(f2.name);
    }
  
    return f1Score - f2Score;
  }

  const facilitiesIndx: number[] = React.useMemo(() => {
    if (!selectedCounty || selectedCounty === "all") return [];

    var facilitiesIndx: number[] = [];
    for (var i = 0; i < counties[selectedCounty].length; i++) {
      if (compareProgramStringToFilter(counties[selectedCounty][i].programs)) {
        facilitiesIndx.push(i);
      }
    }

    return facilitiesIndx.sort((f1, f2) => {
              return sortFacilities(counties[selectedCounty][f1], counties[selectedCounty][f2]);
            });

  }, [counties, programFilters, selectedCounty, compareProgramStringToFilter]);

  const refScrollTarget = useRef<HTMLDivElement>(null);
  const changedFilters = useRef(false);

  const onSelectCounty = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedCounty(value);
    changedFilters.current = true;
  };

  const handleProgramChange = (event: React.SyntheticEvent, value: (string | [FacilityProgramProps, string])[]) => {
    handleSaneFilters(value, sanePrograms, programFilters);
    setProgramFilters(value.map((v) =>
      typeof v === "string" ? v : v[1]
    ));
    changedFilters.current = true;
  }

  useEffect(() => {
    if (changedFilters.current) {
      refScrollTarget.current?.scrollIntoView();
    }
  }, [programFilters, selectedCounty]);

  return (
    <Container
      maxWidth="md"
      sx={{
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(2),
      }}
    >
      {/* since filters are sticky, this gives us a point to scroll into frame */}
      <div ref={refScrollTarget} />
      <Box sx={{position: "sticky", top: 0, backgroundColor: "#ffffff", pt: 3, px: 2, alignItems: "baseline", justifyContent: "center"}}>
        <Typography variant="h5" component="div">
          Facilities
        </Typography>
        <Divider
          sx={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}
        />
        <Box sx={{display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems:"center"}}>
          <FormControl sx={{ m: 1, minWidth: isMobile ? "100%" : 140 }}>
            <InputLabel placeholder="Select">County</InputLabel>
            <Select
              value={selectedCounty ?? ""}
              onChange={onSelectCounty}
              label="County"
              placeholder="Select"
            >
              <MenuItem key="all" value="all">
                All
              </MenuItem>
              <MenuItem divider />
              {ALL_COUNTY_NAMES.sort().map((countyName) => (
                <MenuItem key={countyName} value={countyName}>
                  {countyName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            sx={{ minWidth: isMobile ? "100%" : 200 }}
            value={programFilters}
            limitTags={isMobile ? 1 : -1}
            multiple
            options={programMenuLabels}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  checked={selected}
                />
                {typeof option === "string" ? option : option[1]}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Program(s)" />
            )}
            groupBy={(option) => {
              if (typeof option === "string") {
                switch (option) {
                  case FILTER_ALL_SANE:
                    return "Sane Programs";
                  default:
                    return "Facilities";
                }
              } else {
                return "Sane Programs";
              }
            }}
            getOptionLabel={(option) => typeof option === "string" ? option : option[1]}
            onChange={handleProgramChange}
            isOptionEqualToValue={(option, value) => {
              return typeof option === "string" 
              ? option === value
              : option[1] === value;
            }}
          />
        </Box>
      </Box>
      <div>
        {((selectedCounty && selectedCounty.length > 0) && counties[selectedCounty!]) || selectedCounty === "all" 
          ? selectedCounty === "all" 
            ? (
                Object.values(counties).flatMap((x) => x)
                .map((d: Facility) => {
                  if (compareProgramStringToFilter(d.programs)) {
                    return d;
                  }
                  return undefined;
                }).filter((f): f is TableFacility => Boolean(f))
                .sort(sortFacilities)
                .map((facility, i) => (
                  <TableItem
                    key={i}
                    facility={facility}
                    backgroundColor={i % 2 === 0}
                  />
                ))
              )
            : facilitiesIndx.length > 0 
              ? (
                  facilitiesIndx.map((facilityIndx, i) => (
                    <TableItem
                      key={i}
                      facility={counties[selectedCounty][facilityIndx]}
                      backgroundColor={i % 2 === 0}
                    />
                  ))
                )
              : (
                  <Typography mt={2} variant="h6" color="text.secondary">
                    No facilities meet filter requirements.
                  </Typography>
                )
          : selectedCounty 
            ? (
                <Typography mt={2} variant="h6" color="text.secondary">
                  No forensic nursing program services in this county. Please check
                  neighboring counties for programs.
                </Typography>
              ) 
            : (
                <Box m={3}>
                  <Typography variant="h6" color="text.secondary">
                    Select a county
                  </Typography>
                </Box>
              )
        }
      </div>
    </Container>
  );
}

export default Table;

function priorityScore(facility: TableFacility): number {
  if (facility.saneProgram) {
    return 1;
  }

  switch(facility.type.toLowerCase()) {
    case FACILITY_TYPE_TT.toLowerCase():
      return 2;
    case FACILITY_TYPE_CAC.toLowerCase():
      return 3;
    case FACILITY_TYPE_RCC.toLowerCase():
      return 4;
    case FACILITY_TYPE_OTHER.toLowerCase():
      return 5;
    default:
      return 6;
  } 
}

function handleSaneFilters(value: (string | [FacilityProgramProps, string])[], sanePrograms: (string | [FacilityProgramProps, string])[], programFilter: string[]) {
  const allSaneOptionIndex = value.indexOf(FILTER_ALL_SANE);
  
  // "all sane" option is found in selected value
  if (allSaneOptionIndex >= 0) {
    // a sane program was deselected
    if (sanePrograms.some(p => !value.includes(p[1])) && sanePrograms.every(p => programFilter.includes(p[1]))) {
      // remove "all sane" option
      (value as string[]).splice(allSaneOptionIndex, 1)
    } else {
      // add all sane programs
      for (const saneProgram of sanePrograms) {
        if (!(value as string[]).includes(saneProgram[1])) {
          (value as string[]).push(saneProgram[1]);
        }
      }
    }
    // "all sane" option was deselected and all sane programs were previously selected
  } else if (programFilter.indexOf(FILTER_ALL_SANE) >= 0 && sanePrograms.every(p => programFilter.includes(p[1]))) {
    // remove all sane programs
    for (const saneProgram of sanePrograms) {
      const saneProgramIndex = (value as string[]).indexOf(saneProgram[1]);
      if (saneProgramIndex >= 0) {
        (value as string[]).splice(saneProgramIndex, 1);
      }
    }
  }
}